import { ArrowLeft, ArrowUpRight, Hammer, Sofa, Shield } from "lucide-react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import FloatingWhatsApp from "@/components/FloatingWhatsApp";

// Foto reali dei lavori Dea srl - carpenteria
const RINGHIERA_MONTAGNA = "https://customer-assets.emergentagent.com/job_costruzioni-modern/artifacts/kxfdpnsf_recinzione%20fasce.webp";
const CANCELLI_HARLEY = "https://customer-assets.emergentagent.com/job_costruzioni-modern/artifacts/a1k3eq0c_Recinzione%20in%20lamiera%20taglio%20laser%20color%20marrone%20brune%20sabl%C3%A9.jpg";
const TAVOLO_SPIDER = "https://customer-assets.emergentagent.com/job_costruzioni-modern/artifacts/yutqy4rw_tavolo2.jpg";

// Esempi tipologie realizzabili (cataloghi/stili di riferimento)
const CATALOGO_LAMIERA = "https://customer-assets.emergentagent.com/job_costruzioni-modern/artifacts/p6x1dnuj_recinzione%20taglio%20laser.webp";
const CATALOGO_PANNELLI = "https://customer-assets.emergentagent.com/job_costruzioni-modern/artifacts/dqwiwrmc_Recinzioni%20in%20pannelli%20in%20lamiera%20a%20taglio%20laser%20su%20misura.jpg";

const CATEGORIE = [
  {
    id: "recinzioni",
    title: "Recinzioni & Cancelli",
    subtitle: "Design moderno · Taglio laser · Su misura",
    icon: Shield,
    intro:
      "Recinzioni e cancelli in lamiera con taglio laser personalizzato, verniciati a polvere per resistere agli agenti atmosferici. Realizziamo pattern decorativi, loghi e disegni su richiesta del cliente.",
    projects: [
      {
        img: CANCELLI_HARLEY,
        title: "Cancelli in lamiera con taglio laser personalizzato",
        desc: "Cancelli a taglio laser con pattern decorativi e logo Harley-Davidson inciso al centro. Verniciatura marrone testa di moro sablé, montaggio con pilastri verniciati coordinati.",
        tags: ["Taglio laser", "Verniciatura", "Su misura"],
      },
      {
        img: RINGHIERA_MONTAGNA,
        title: "Ringhiera con fasce orizzontali su muretto in pietra",
        desc: "Ringhiera moderna in ferro verniciato grigio antracite, con fasce orizzontali su misura. Installazione ancorata a muretto in pietra locale — soluzione per esterni in zona alpina.",
        tags: ["Ferro verniciato", "Design moderno", "Esterni"],
      },
    ],
  },
  {
    id: "arredo",
    title: "Arredo Industriale · Ferro + Legno",
    subtitle: "Tavoli · Consolle · Complementi",
    icon: Sofa,
    intro:
      "Complementi d'arredo con struttura in ferro nero verniciato e piani in legno massello. Ogni pezzo è progettato e realizzato nella nostra officina interna dal fabbro dedicato.",
    projects: [
      {
        img: TAVOLO_SPIDER,
        title: "Tavolo massello con base ferro Spider",
        desc: "Tavolo da pranzo con piano in rovere massello dai bordi naturali (live edge) e base scultorea in ferro nero verniciato a forma di 'X spider'. Un pezzo unico, realizzato su misura per lo spazio del cliente.",
        tags: ["Rovere massello", "Base ferro nero", "Live edge"],
      },
    ],
  },
];

const CATALOGO_TIPOLOGIE = [
  {
    img: CATALOGO_LAMIERA,
    label: "Recinzione doghe orizzontali",
    note: "Stile disponibile in vari colori",
  },
  {
    img: CATALOGO_PANNELLI,
    label: "Pannello taglio laser su misura",
    note: "Design personalizzabile",
  },
];

export default function CarpenteriaPortfolio() {
  return (
    <div
      data-testid="carpenteria-portfolio-page"
      className="bg-[#050B14] text-slate-100 min-h-screen"
    >
      <Navbar />

      {/* Hero */}
      <header className="relative pt-32 md:pt-44 pb-16 md:pb-20 border-b border-[#1E293B] overflow-hidden">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10">
          <a
            href="/"
            data-testid="back-home"
            className="inline-flex items-center gap-2 text-slate-400 hover:text-white text-sm mb-8"
          >
            <ArrowLeft size={16} /> Torna al sito
          </a>

          <div className="flex items-center gap-3 mb-5">
            <div className="w-10 h-px bg-[#FF5A1F]" />
            <span className="text-xs tracking-[0.3em] uppercase text-[#FF5A1F] font-bold">
              Portfolio Carpenteria
            </span>
          </div>

          <h1 className="headline text-white text-5xl md:text-7xl max-w-4xl">
            Il ferro,{" "}
            <span className="font-display italic font-medium text-slate-300">
              lavorato in casa nostra.
            </span>
          </h1>

          <p className="mt-8 max-w-2xl text-slate-400 text-base md:text-lg leading-relaxed">
            Nella nostra officina interna a Garlasco, il nostro fabbro dedicato
            realizza recinzioni, cancelli, ringhiere e arredo industriale su
            misura. Taglio laser, saldatura, verniciatura a polvere: tutto
            gestito internamente.
          </p>

          <div className="mt-10 flex flex-col sm:flex-row gap-3">
            <a
              href="/#preventivo"
              data-testid="hero-cta"
              className="inline-flex items-center gap-3 bg-[#FF5A1F] hover:bg-[#EA450C] text-white px-6 py-4 text-sm font-semibold transition-colors"
            >
              Richiedi preventivo carpenteria
              <ArrowUpRight size={18} />
            </a>
            <a
              href="https://wa.me/393393015941?text=Ciao%20Dea%20srl%2C%20ho%20visto%20il%20portfolio%20di%20carpenteria%20e%20vorrei%20maggiori%20informazioni."
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-3 border border-slate-500 hover:border-white text-white px-6 py-4 text-sm font-semibold transition-colors"
            >
              Scrivici su WhatsApp
              <ArrowUpRight size={18} />
            </a>
          </div>
        </div>
      </header>

      {/* Categorie con foto reali */}
      <main className="max-w-[1400px] mx-auto px-6 md:px-10 py-20 md:py-28 space-y-24 md:space-y-32">
        {CATEGORIE.map((cat, idx) => {
          const Icon = cat.icon;
          return (
            <section
              key={cat.id}
              id={cat.id}
              data-testid={`section-${cat.id}`}
            >
              {/* Section header */}
              <div className="grid md:grid-cols-12 gap-8 mb-12 items-end">
                <div className="md:col-span-7">
                  <div className="flex items-center gap-4 mb-4">
                    <div className="w-12 h-12 bg-[#FF5A1F] flex items-center justify-center flex-shrink-0">
                      <Icon size={22} className="text-white" strokeWidth={2} />
                    </div>
                    <span className="font-mono text-xs tracking-[0.3em] text-slate-500 uppercase">
                      / 0{idx + 1}
                    </span>
                  </div>
                  <h2 className="headline text-white text-3xl md:text-5xl">
                    {cat.title.split(" · ")[0].split(" & ")[0]}{" "}
                    <span className="font-display italic font-medium text-slate-300">
                      {cat.title.includes(" · ")
                        ? cat.title.split(" · ")[1]
                        : cat.title.includes(" & ")
                        ? "& " + cat.title.split(" & ")[1]
                        : ""}
                    </span>
                  </h2>
                  <p className="mt-4 text-xs tracking-[0.25em] uppercase text-slate-500 font-bold">
                    {cat.subtitle}
                  </p>
                </div>
                <div className="md:col-span-5">
                  <p className="text-slate-400 leading-relaxed">{cat.intro}</p>
                </div>
              </div>

              {/* Projects */}
              <div className="space-y-16">
                {cat.projects.map((p, i) => (
                  <article
                    key={i}
                    data-testid={`project-${cat.id}-${i}`}
                    className="grid md:grid-cols-12 gap-8 md:gap-12 items-center"
                  >
                    <div
                      className={`md:col-span-7 ${
                        i % 2 === 1 ? "md:order-2" : ""
                      }`}
                    >
                      <div className="relative aspect-[4/3] md:aspect-[16/10] overflow-hidden bg-[#111C30] border border-[#1E293B] group">
                        <img
                          src={p.img}
                          alt={p.title}
                          loading="lazy"
                          className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                          style={{
                            filter: "brightness(1.05) contrast(1.05)",
                          }}
                        />
                      </div>
                    </div>
                    <div
                      className={`md:col-span-5 ${
                        i % 2 === 1 ? "md:order-1" : ""
                      }`}
                    >
                      <div className="font-mono text-xs tracking-[0.25em] text-slate-500 uppercase mb-3">
                        / 0{i + 1} · Lavoro realizzato
                      </div>
                      <h3 className="text-2xl md:text-3xl font-bold text-white tracking-tight leading-tight">
                        {p.title}
                      </h3>
                      <p className="mt-4 text-slate-400 leading-relaxed">
                        {p.desc}
                      </p>
                      <div className="mt-6 flex flex-wrap gap-2">
                        {p.tags.map((t) => (
                          <span
                            key={t}
                            className="text-[11px] tracking-wider uppercase border border-slate-600 text-slate-300 px-2.5 py-1"
                          >
                            {t}
                          </span>
                        ))}
                      </div>
                    </div>
                  </article>
                ))}
              </div>
            </section>
          );
        })}

        {/* Catalogo tipologie */}
        <section id="catalogo" data-testid="section-catalogo">
          <div className="grid md:grid-cols-12 gap-8 mb-10 items-end">
            <div className="md:col-span-7">
              <div className="flex items-center gap-4 mb-4">
                <div className="w-12 h-12 bg-[#FF5A1F]/15 border border-[#FF5A1F]/40 flex items-center justify-center flex-shrink-0">
                  <Hammer size={22} className="text-[#FF5A1F]" strokeWidth={2} />
                </div>
                <span className="font-mono text-xs tracking-[0.3em] text-slate-500 uppercase">
                  / 03
                </span>
              </div>
              <h2 className="headline text-white text-3xl md:text-5xl">
                Tipologie{" "}
                <span className="font-display italic font-medium text-slate-300">
                  realizzabili.
                </span>
              </h2>
            </div>
            <div className="md:col-span-5">
              <p className="text-slate-400 leading-relaxed">
                Esempi di stili e tipologie che possiamo replicare in officina
                su misura per te. Contattaci per un preventivo personalizzato.
              </p>
            </div>
          </div>

          <div className="grid md:grid-cols-2 gap-5">
            {CATALOGO_TIPOLOGIE.map((c, i) => (
              <article
                key={i}
                data-testid={`catalogo-${i}`}
                className="relative bg-[#0A1120] border border-[#1E293B] overflow-hidden group"
              >
                <div className="relative aspect-[4/3] overflow-hidden">
                  <img
                    src={c.img}
                    alt={c.label}
                    loading="lazy"
                    className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                  />
                </div>
                <div className="p-5 md:p-6 border-t border-[#1E293B]">
                  <div className="text-xs tracking-[0.25em] uppercase text-slate-500 mb-1 font-bold">
                    Catalogo
                  </div>
                  <div className="text-white font-bold text-lg">{c.label}</div>
                  <div className="text-slate-400 text-sm mt-1">{c.note}</div>
                </div>
              </article>
            ))}
          </div>
        </section>

        {/* CTA finale */}
        <section
          data-testid="portfolio-cta"
          className="relative bg-[#0A1120] border border-[#1E293B] p-8 md:p-16"
        >
          <div className="grid md:grid-cols-12 gap-8 items-center">
            <div className="md:col-span-8">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-px bg-[#FF5A1F]" />
                <span className="text-xs tracking-[0.3em] uppercase text-[#FF5A1F] font-bold">
                  Progettiamo insieme
                </span>
              </div>
              <h2 className="headline text-white text-3xl md:text-5xl">
                Hai un progetto in mente?{" "}
                <span className="font-display italic font-medium text-slate-300">
                  Realizziamolo.
                </span>
              </h2>
              <p className="mt-5 text-slate-400 leading-relaxed max-w-xl">
                Portaci un'idea, un disegno, una foto: il nostro fabbro la
                trasforma in un pezzo su misura. Preventivo e sopralluogo gratuiti.
              </p>
            </div>
            <div className="md:col-span-4 flex flex-col gap-3">
              <a
                href="/#preventivo"
                className="inline-flex items-center justify-between gap-3 bg-[#FF5A1F] hover:bg-[#EA450C] text-white px-6 py-4 text-sm font-semibold transition-colors"
              >
                Richiedi preventivo
                <ArrowUpRight size={18} />
              </a>
              <a
                href="https://wa.me/393393015941?text=Ciao%20Dea%20srl%2C%20vorrei%20un%20preventivo%20per%20un%20lavoro%20di%20carpenteria."
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-between gap-3 bg-[#25D366] hover:bg-[#1FBA56] text-white px-6 py-4 text-sm font-semibold transition-colors"
              >
                Scrivi su WhatsApp
                <ArrowUpRight size={18} />
              </a>
            </div>
          </div>
        </section>
      </main>

      <Footer />
      <FloatingWhatsApp />
    </div>
  );
}
